import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/brand/BrandMark";

const NAV = [
    { to: "/how-it-works", label: "How It Works" },
    { to: "/pricing", label: "Pricing" },
    { to: "/tools", label: "Tools" },
    { to: "/for-investors", label: "For Investors" },
    { to: "/learn", label: "Learn" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <header
            className="sticky top-0 z-40 bg-bl-bg/85 backdrop-blur-md border-b border-bl"
            data-testid="navbar"
        >
            <div className="bl-container flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    className="flex items-center gap-2.5 group"
                    data-testid="logo-link"
                >
                    <BrandMark size={36} className="shrink-0" />
                    <div className="leading-tight">
                        <div className="font-display font-semibold text-[15px] text-bl tracking-tight">
                            Buyer's Leverage
                        </div>
                        <div className="text-[10px] tracking-[0.22em] text-bl-muted uppercase">
                            Inspection Intelligence
                        </div>
                    </div>
                </Link>

                <nav
                    className="hidden lg:flex items-center gap-1"
                    aria-label="Primary"
                >
                    {NAV.map((n) => {
                        const active = location.pathname.startsWith(n.to);
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.to.replace("/", "")}-link`}
                                aria-current={active ? "page" : undefined}
                                className={
                                    "px-4 py-2 text-sm rounded-full transition-colors " +
                                    (active
                                        ? "text-bl bg-[var(--bl-teal-tint)]"
                                        : "text-bl-muted hover:text-bl hover:bg-[var(--bl-teal-tint)]")
                                }
                            >
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                className="bl-btn-ghost"
                                data-testid="dashboard-link"
                            >
                                Dashboard
                            </Link>
                            <Link
                                to="/account"
                                className="bl-btn-ghost"
                                data-testid="account-link"
                            >
                                Account
                            </Link>
                            <button
                                className="bl-btn-ghost"
                                onClick={async () => {
                                    await logout();
                                    navigate("/");
                                }}
                                data-testid="logout-btn"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/auth/login"
                            className="bl-btn-ghost"
                            data-testid="signin-link"
                        >
                            Sign In
                        </Link>
                    )}
                    <Link
                        to="/upload"
                        className="bl-btn-primary"
                        data-testid="nav-upload-cta"
                    >
                        Upload Inspection <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>

                <button
                    className="lg:hidden p-2 rounded-md text-bl"
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-bl bg-bl-surface">
                    <div className="bl-container py-6 flex flex-col gap-2">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.to.replace("/", "")}-link`}
                                className="px-3 py-2 text-bl text-base rounded-md hover:bg-[var(--bl-teal-tint)]"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <div className="h-px bg-bl-border my-2" />
                        {user ? (
                            <>
                                <Link
                                    to="/dashboard"
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-2 text-bl text-base rounded-md hover:bg-[var(--bl-teal-tint)]"
                                    data-testid="mobile-dashboard-link"
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/account"
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-2 text-bl text-base rounded-md hover:bg-[var(--bl-teal-tint)]"
                                    data-testid="mobile-account-link"
                                >
                                    Account settings
                                </Link>
                                <button
                                    className="text-left px-3 py-2 text-bl text-base rounded-md hover:bg-[var(--bl-teal-tint)]"
                                    onClick={async () => {
                                        await logout();
                                        setOpen(false);
                                        navigate("/");
                                    }}
                                    data-testid="mobile-logout-btn"
                                >
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/auth/login"
                                onClick={() => setOpen(false)}
                                className="px-3 py-2 text-bl text-base rounded-md hover:bg-[var(--bl-teal-tint)]"
                                data-testid="mobile-signin-link"
                            >
                                Sign In
                            </Link>
                        )}
                        <Link
                            to="/upload"
                            onClick={() => setOpen(false)}
                            className="bl-btn-primary mt-2"
                            data-testid="mobile-upload-cta"
                        >
                            Upload Inspection
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
